/**
 * Typed errors thrown by queries, mutators and their permission helpers.
 *
 * Every entry point (Zero push, REST, MCP) maps them to its own transport:
 * Zero error results, RFC 9457 problem details, MCP tool errors. Untyped
 * errors are treated as a generic rejection.
 */
export class OperationError extends Error {
}
/** The caller is not allowed to perform the operation. */
export class ForbiddenError extends OperationError {
    code = "forbidden";
    status = 403;
    constructor(message = "Forbidden") {
        super(message);
        this.name = "ForbiddenError";
    }
}
/** A row the operation depends on does not exist. */
export class NotFoundError extends OperationError {
    code = "not_found";
    status = 404;
    constructor(message = "Not found") {
        super(message);
        this.name = "NotFoundError";
    }
}
/** The operation conflicts with the current state. */
export class ConflictError extends OperationError {
    code = "conflict";
    status = 409;
    constructor(message = "Conflict") {
        super(message);
        this.name = "ConflictError";
    }
}
/** The operation's args failed validation. */
export class ValidationError extends OperationError {
    issues;
    code = "validation_failed";
    status = 400;
    constructor(message, issues = []) {
        super(message);
        this.issues = issues;
        this.name = "ValidationError";
    }
}
const errorNames = {
    ForbiddenError: "forbidden",
    NotFoundError: "not_found",
    ConflictError: "conflict",
    ValidationError: "validation_failed",
};
/**
 * Returns the typed error code of `err`, also for errors from another copy of
 * this module (e.g. a duplicated bundle), which `instanceof` would miss.
 */
export function operationErrorCode(err) {
    if (err instanceof OperationError)
        return err.code;
    if (err instanceof Error) {
        const code = err.code;
        if (typeof code === "string" && errorNames[err.name] === code) {
            return code;
        }
    }
    return null;
}
